import React from 'react';
import {
  getCentralizedCompanyListCache,
  getCompanyListCache,
  getCompanySearchFilterCache,
  getLightWeightCentralizedCompanyListCache,
  getLightWeightCompanySearchFilterCache,
  getPartnerStackCompanyListCache,
  getPartnerStackCompanySearchFilterCache,
  getThemeSettingCache
} from '../reusable/CacheHelper';

// ✅ Define the type for `selections`
type Selections = {
  companyOwners: any[];
  allCmsLeadSources: any[];
  allCmsIndustries: any[];
  salesContactOwners: any[];
  allSubscriptionPlans: any[];
  allAddOnPlans: any[];
  allSubscriptionPlansIncludedOldPlans: any[];
  allAddOnPlansIncludedOldPlans: any[];
};

// ✅ Define the full AppContext type
export interface AppContextType {
  themeSetting: ReturnType<typeof getThemeSettingCache>;
  companyFilter: ReturnType<typeof getCompanySearchFilterCache>;
  lightWeightCompanyFilter: ReturnType<
    typeof getLightWeightCompanySearchFilterCache
  >;
  partnerStackCompanyFilter: ReturnType<
    typeof getPartnerStackCompanySearchFilterCache
  >;
  companyList: ReturnType<typeof getCompanyListCache>;
  centralizedCompanyList: ReturnType<typeof getCentralizedCompanyListCache>;
  lightWeightCentralizedCompanyList: ReturnType<
    typeof getLightWeightCentralizedCompanyListCache
  >;
  partnerStackCompanyList: ReturnType<typeof getPartnerStackCompanyListCache>;
  setAppContextValue: (
    value:
      | Partial<AppContextType>
      | ((prev: AppContextType) => AppContextType)
  ) => void;
  companiesWatchlist: any[];
  whatsapp360dialogPartnerChannels: any[];
  chatApiInstanceList: any[];
  facebookMessengerInstanceList: any[];
  stripeSleekPayCompanies: any[];
  stripeSleekPayReportDatas: any[];
  twilioUsages: any | null;
  subscriptionPlanResponse: any | null;
  whatsappCloudApi: any | null;
  selections: Selections | null;
  partnerStackSelections: any | null;
  analyticData: any | null;
  companyAdditionalInfosDict: Record<string, any>;
  analyticDataDict: Record<string, any>;
  accruedAnalyticData: any | null;
  distributionAnalyticData: any | null;
  salesAnalyticData: any | null;
  companyOwnerAnalyticData: any | null;
  csOwnerAnalyticData: any | null;
  partnerStackGroupAnalyticData: any | null;
  centralizedCompanyAdditionalInfos: any | null;
  centralizedAnalyticData: any | null;
  centralizedAccruedAnalyticData: any | null;
  centralizedDistributionAnalyticData: any | null;
  centralizedSalesAnalyticData: any | null;
  centralizedCompanyOwnerAnalyticData: any | null;
  centralizedCsOwnerAnalyticData: any | null;
  centralizedPartnerStackGroupAnalyticData: any | null;
  whatsappApplications: any | null;
  companyAdditionalInfos: any | null;
  resellerData: {
    resellerCompanyList: any[];
    resellerClientCompanyList: any[];
  };
  publicApiGatewayData: {
    sleekflowCompanies: any[];
  };
  isUnauthorized: boolean;
  currencyExchangeRates: any[] | null;
  salesAttainmentUserRoles: string[] | null;
}

// ✅ Create a properly typed default value
export const AppContextDefaultValue: AppContextType = {
  themeSetting: getThemeSettingCache(),
  companyFilter: getCompanySearchFilterCache(),
  lightWeightCompanyFilter: getLightWeightCompanySearchFilterCache(),
  partnerStackCompanyFilter: getPartnerStackCompanySearchFilterCache(),
  companyList: getCompanyListCache(),
  centralizedCompanyList: getCentralizedCompanyListCache(),
  lightWeightCentralizedCompanyList:
    getLightWeightCentralizedCompanyListCache(),
  partnerStackCompanyList: getPartnerStackCompanyListCache(),
  setAppContextValue: (
    value: Partial<AppContextType> | ((prev: AppContextType) => AppContextType)
  ) => {
    // This is just a placeholder implementation for the default context value
    // The actual implementation is provided by the useState hook in App.js
    // In App.js, it's implemented as:
    // const [appContextValue, setAppContextValue] = useState(AppContextDefaultValue);
    // <AppContext.Provider value={{ ...appContextValue, setAppContextValue }}>
    //
    // When components use this function, they typically call it like:
    // appContext.setAppContextValue({
    //   ...appContext,
    //   someProperty: newValue,
    //   anotherProperty: anotherNewValue
    // });
    //
    // This spreads the current context values and overrides specific properties
    console.log('setAppContextValue called with:', value);
  },
  companiesWatchlist: new Array(5),
  whatsapp360dialogPartnerChannels: new Array(2),
  chatApiInstanceList: [],
  facebookMessengerInstanceList: [],
  stripeSleekPayCompanies: [],
  stripeSleekPayReportDatas: [],
  twilioUsages: null,
  subscriptionPlanResponse: null,
  whatsappCloudApi: null,
  selections: null, // 🔥 Now TS knows this will be Selections | null
  partnerStackSelections: null,
  analyticData: null,
  companyAdditionalInfosDict: {},
  analyticDataDict: {},
  accruedAnalyticData: null,
  distributionAnalyticData: null,
  salesAnalyticData: null,
  companyOwnerAnalyticData: null,
  csOwnerAnalyticData: null,
  partnerStackGroupAnalyticData: null,
  centralizedCompanyAdditionalInfos: null,
  centralizedAnalyticData: null,
  centralizedAccruedAnalyticData: null,
  centralizedDistributionAnalyticData: null,
  centralizedSalesAnalyticData: null,
  centralizedCompanyOwnerAnalyticData: null,
  centralizedCsOwnerAnalyticData: null,
  centralizedPartnerStackGroupAnalyticData: null,
  whatsappApplications: null,
  companyAdditionalInfos: null,
  resellerData: {
    resellerCompanyList: [],
    resellerClientCompanyList: []
  },
  publicApiGatewayData: {
    sleekflowCompanies: []
  },
  isUnauthorized: false,
  currencyExchangeRates: null,
  salesAttainmentUserRoles: null
};

// ✅ Create the context with proper type
export const AppContext = React.createContext<AppContextType>(
  AppContextDefaultValue
);

